import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { ConfirmarDialog, FP, fpInputSx } from 'components/financepro';
import { useApi } from 'lib/api';
import { useProductosMaterialContext } from 'context';

export const FORMULARIO_ID_DESCRIPCION_VACIO = {
  id: null,
  descripcion: '',
};

const cardShellSx = {
  borderRadius: 2.5,
  border: `1px solid ${FP.color.border}`,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
  overflow: 'hidden',
};

const filterHeaderSx = {
  px: { xs: 2, md: 3 },
  py: 1.6,
  borderBottom: `1px solid ${FP.color.border}`,
  bgcolor: '#FAFBFE',
  display: 'flex',
  alignItems: 'center',
  gap: 1.2,
};

const cellHeadSx = {
  fontWeight: 700,
  fontSize: '0.75rem',
  color: FP.color.textSecondary,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  display: 'block',
  mb: 0.5,
};

const btnPrimarySx = {
  height: 38,
  bgcolor: FP.color.navy,
  color: '#FFFFFF',
  borderRadius: 1.4,
  textTransform: 'none',
  fontWeight: 700,
  boxShadow: 'none',
  '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
  '&.Mui-disabled': { bgcolor: FP.color.border, color: FP.color.textMuted },
};

const btnOutlinedSx = {
  height: 38,
  borderRadius: 1.4,
  textTransform: 'none',
  fontWeight: 700,
  borderColor: FP.color.border,
  color: FP.color.navy,
  '&:hover': { borderColor: FP.color.navy, bgcolor: FP.color.surfaceAlt },
};

const btnBuscarSx = {
  ...btnOutlinedSx,
  height: 46,
  minWidth: 160,
  px: 2.5,
  fontSize: '0.95rem',
};

/**
 * Formulario reutilizable de catálogos id + descripción.
 */
export function CatalogoIdDescripcionForm({
  value = FORMULARIO_ID_DESCRIPCION_VACIO,
  onChange,
  disabled = false,
  etiquetaDescripcion = 'Descripción',
}) {
  const actualizar = (campo, valor) => {
    if (!onChange) return;
    onChange({ ...value, [campo]: valor });
  };

  return (
    <Grid container spacing={2} sx={{ pt: 0.5 }}>
      <Grid item xs={12} sm={4}>
        <TextField
          fullWidth
          size="small"
          label="Id"
          value={value.id != null && value.id !== '' ? String(value.id) : ''}
          disabled
          InputLabelProps={{ shrink: true }}
          helperText={value.id ? 'Generado al guardar' : 'Se asigna al guardar'}
          sx={fpInputSx}
        />
      </Grid>
      <Grid item xs={12} sm={8}>
        <TextField
          fullWidth
          size="small"
          label={etiquetaDescripcion}
          value={value.descripcion || ''}
          onChange={(e) => actualizar('descripcion', e.target.value)}
          disabled={disabled}
          inputProps={{ maxLength: 255 }}
          InputLabelProps={{ shrink: true }}
          sx={fpInputSx}
        />
      </Grid>
    </Grid>
  );
}

/**
 * Catálogo reutilizable con id y descripción.
 * Se usa como diálogo (`open` / `onClose`) o embebido (`embedded`).
 */
const CatalogoIdDescripcion = ({
  open = false,
  onClose,
  embedded = false,
  puedeModificar = false,
  puedeEliminar,
  titulo,
  singular,
  icono,
  listPath,
  savePath,
  deletePath,
  listKey,
}) => {
  const { selectedCompany, showSuccess, showError } = useProductosMaterialContext();
  const { post } = useApi();
  const puedeBorrar = puedeEliminar ?? puedeModificar;
  const puedeOperarDb = Boolean(selectedCompany?.db_empresa);
  const Icono = icono;

  const [filas, setFilas] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [formulario, setFormulario] = useState(FORMULARIO_ID_DESCRIPCION_VACIO);
  const [dialogoFormulario, setDialogoFormulario] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [eliminarId, setEliminarId] = useState(null);
  const [eliminando, setEliminando] = useState(false);

  const cargarLista = useCallback(async () => {
    if (!puedeOperarDb) {
      setFilas([]);
      return;
    }
    setCargando(true);
    try {
      const { ok, data } = await post(listPath, {
        id_empresa: selectedCompany,
        busqueda,
      });
      if (!ok) {
        showError(data.Error || `No se pudo cargar ${titulo.toLowerCase()}`);
        return;
      }
      setFilas(Array.isArray(data[listKey]) ? data[listKey] : []);
    } catch (e) {
      showError(`Error de conexión al cargar ${titulo.toLowerCase()}`);
    } finally {
      setCargando(false);
    }
  }, [puedeOperarDb, post, selectedCompany, busqueda, showError, listPath, listKey, titulo]);

  useEffect(() => {
    if ((open || embedded) && puedeOperarDb) {
      cargarLista();
    }
    if (!open && !embedded) {
      setDialogoFormulario(false);
      setFormulario(FORMULARIO_ID_DESCRIPCION_VACIO);
      setEliminarId(null);
      setBusqueda('');
    }
  }, [open, embedded, puedeOperarDb, selectedCompany?.db_empresa]);

  const abrirNuevo = () => {
    if (!puedeModificar) {
      showError(`No tiene permiso para modificar ${titulo.toLowerCase()}`);
      return;
    }
    setFormulario(FORMULARIO_ID_DESCRIPCION_VACIO);
    setDialogoFormulario(true);
  };

  const abrirEditar = (fila) => {
    setFormulario({
      id: fila.id,
      descripcion: fila.descripcion ?? '',
    });
    setDialogoFormulario(true);
  };

  const cerrarFormulario = () => {
    if (guardando) return;
    setDialogoFormulario(false);
    setFormulario(FORMULARIO_ID_DESCRIPCION_VACIO);
  };

  const guardar = async () => {
    if (!puedeModificar) {
      showError(`No tiene permiso para modificar ${titulo.toLowerCase()}`);
      return;
    }
    if (!puedeOperarDb) {
      showError('Seleccione una empresa con base de datos');
      return;
    }
    const descripcion = (formulario.descripcion || '').trim();
    if (!descripcion) {
      showError('La descripción es obligatoria');
      return;
    }

    setGuardando(true);
    try {
      const cuerpo = {
        id_empresa: selectedCompany,
        descripcion,
      };
      if (formulario.id != null) cuerpo.id = formulario.id;
      const { ok, data } = await post(savePath, cuerpo);
      if (!ok) {
        showError(data.Error || `No se pudo guardar ${singular}`);
        return;
      }
      showSuccess(data.mensaje || 'Registro guardado');
      cerrarFormulario();
      await cargarLista();
    } catch (e) {
      showError('Error de conexión al guardar');
    } finally {
      setGuardando(false);
    }
  };

  const confirmarEliminar = async () => {
    if (!puedeBorrar) {
      showError(`No tiene permiso para eliminar ${titulo.toLowerCase()}`);
      return;
    }
    if (eliminarId == null) return;
    setEliminando(true);
    try {
      const { ok, data } = await post(deletePath, {
        id_empresa: selectedCompany,
        id: eliminarId,
      });
      if (!ok) {
        showError(data.Error || 'No se pudo eliminar');
        return;
      }
      showSuccess(data.mensaje || 'Registro eliminado');
      if (dialogoFormulario && formulario.id === eliminarId) {
        cerrarFormulario();
      }
      setEliminarId(null);
      await cargarLista();
    } catch (e) {
      showError('Error de conexión al eliminar');
    } finally {
      setEliminando(false);
    }
  };

  const cerrarCatalogo = () => {
    if (guardando || eliminando) return;
    if (onClose) onClose();
  };

  const cuerpo = (
    <>
      <Card elevation={0} sx={{ ...cardShellSx, mb: 2 }}>
        <Box sx={filterHeaderSx}>
          <Box
            sx={{
              width: 30,
              height: 30,
              borderRadius: 1.2,
              bgcolor: FP.color.accentBlueSoft,
              color: FP.color.accentBlue,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {Icono ? <Icono sx={{ fontSize: 18 }} /> : null}
          </Box>
          <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
            {titulo}
          </Typography>
        </Box>
        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={abrirNuevo}
              disabled={!puedeModificar}
              sx={btnPrimarySx}
            >
              Añadir {singular}
            </Button>
            <TextField
              size="small"
              label="Buscar"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && cargarLista()}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: { xs: 220, sm: 360, md: 480 }, flex: { md: 1 }, maxWidth: 640, ...fpInputSx }}
            />
            <Button
              variant="outlined"
              startIcon={<SearchIcon />}
              onClick={cargarLista}
              disabled={cargando}
              sx={btnBuscarSx}
            >
              Consultar
            </Button>
            <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.85rem', ml: 'auto' }}>
              {cargando ? 'Cargando…' : `${filas.length} registro(s)`}
            </Typography>
          </Box>
        </CardContent>
      </Card>

      <Card elevation={0} sx={cardShellSx}>
        <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{
              border: `1px solid ${FP.color.border}`,
              borderRadius: 2,
              overflow: 'auto',
              minHeight: { xs: 360, md: 480 },
              maxHeight: { xs: '52vh', md: '58vh' },
            }}
          >
            <Table size="small" sx={{ minWidth: 640 }}>
              <TableBody>
                <TableRow
                  sx={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 2,
                    '& td': {
                      border: 'none',
                      borderBottom: `2px solid ${FP.color.border}`,
                      bgcolor: FP.color.surfaceAlt,
                    },
                  }}
                >
                  <TableCell>
                    <Typography component="span" sx={cellHeadSx}>
                      Id
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography component="span" sx={cellHeadSx}>
                      Descripción
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography
                      component="span"
                      sx={{ ...cellHeadSx, textAlign: 'right', width: '100%' }}
                    >
                      Acciones
                    </Typography>
                  </TableCell>
                </TableRow>
                {filas.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ py: 6, color: FP.color.textSecondary }}>
                      {cargando ? 'Cargando…' : `No hay ${titulo.toLowerCase()}.`}
                    </TableCell>
                  </TableRow>
                ) : (
                  filas.map((row) => (
                    <TableRow key={row.id} hover sx={{ cursor: 'pointer' }}>
                      <TableCell onClick={() => abrirEditar(row)}>{row.id}</TableCell>
                      <TableCell onClick={() => abrirEditar(row)}>{row.descripcion}</TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => abrirEditar(row)}
                          disabled={!puedeModificar}
                          title="Editar"
                          sx={{
                            color: FP.color.accentBlue,
                            '&:hover': { bgcolor: FP.color.accentBlueSoft },
                            '&.Mui-disabled': { color: FP.color.textMuted },
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setEliminarId(row.id);
                          }}
                          disabled={!puedeBorrar}
                          title="Eliminar"
                          sx={{
                            color: FP.color.danger,
                            '&:hover': { bgcolor: FP.color.dangerSoft },
                            '&.Mui-disabled': { color: FP.color.textMuted },
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </>
  );

  const dialogoAlta = (
    <Dialog
      open={dialogoFormulario}
      onClose={cerrarFormulario}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2.5,
          border: `1px solid ${FP.color.border}`,
          boxShadow: FP.shadow.md,
          overflow: 'hidden',
          width: { xs: '94vw', md: 760 },
          maxWidth: 760,
        },
      }}
    >
      <DialogTitle
        sx={{
          px: { xs: 2, md: 3 },
          py: 1.6,
          borderBottom: `1px solid ${FP.color.border}`,
          bgcolor: '#FAFBFE',
          display: 'flex',
          alignItems: 'center',
          gap: 1.2,
        }}
      >
        <Box
          sx={{
            width: 30,
            height: 30,
            borderRadius: 1.2,
            bgcolor: FP.color.accentBlueSoft,
            color: FP.color.accentBlue,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {Icono ? <Icono sx={{ fontSize: 18 }} /> : null}
        </Box>
        <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
          {formulario.id ? `Editar ${singular}` : `Añadir ${singular}`}
        </Typography>
      </DialogTitle>
      <DialogContent dividers sx={{ p: { xs: 2, md: 3 } }}>
        <CatalogoIdDescripcionForm
          value={formulario}
          onChange={setFormulario}
          disabled={!puedeModificar}
        />
      </DialogContent>
      <DialogActions
        sx={{
          px: { xs: 2, md: 3 },
          py: 1.5,
          borderTop: `1px solid ${FP.color.border}`,
          bgcolor: '#FAFBFE',
          gap: 1,
        }}
      >
        <Button
          onClick={cerrarFormulario}
          startIcon={<CloseIcon />}
          disabled={guardando}
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            color: FP.color.textSecondary,
            borderRadius: 1.4,
            '&:hover': { bgcolor: FP.color.surfaceAlt },
          }}
        >
          Cancelar
        </Button>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={guardar}
          disabled={!puedeModificar || guardando}
          sx={btnPrimarySx}
        >
          {formulario.id ? 'Actualizar' : 'Guardar'}
        </Button>
      </DialogActions>
    </Dialog>
  );

  const confirmacion = (
    <ConfirmarDialog
      abierto={eliminarId != null}
      titulo={`Eliminar ${singular}`}
      destructivo
      textoConfirmar="Eliminar"
      mensaje={`¿Eliminar este ${singular} del catálogo?`}
      cargando={eliminando}
      onCerrar={() => !eliminando && setEliminarId(null)}
      onConfirmar={confirmarEliminar}
    />
  );

  if (embedded) {
    return (
      <Box>
        {cuerpo}
        {dialogoAlta}
        {confirmacion}
      </Box>
    );
  }

  return (
    <>
      <Dialog
        open={Boolean(open)}
        onClose={cerrarCatalogo}
        maxWidth="xl"
        fullWidth
        scroll="paper"
        PaperProps={{
          sx: {
            borderRadius: 2.5,
            border: `1px solid ${FP.color.border}`,
            boxShadow: FP.shadow.md,
            overflow: 'hidden',
            width: '96vw',
            maxWidth: 1400,
            minHeight: { xs: '78vh', md: '84vh' },
          },
        }}
      >
        <DialogTitle
          sx={{
            px: { xs: 2, md: 3 },
            py: 1.6,
            borderBottom: `1px solid ${FP.color.border}`,
            bgcolor: '#FAFBFE',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: 1.2,
                bgcolor: FP.color.accentBlueSoft,
                color: FP.color.accentBlue,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {Icono ? <Icono sx={{ fontSize: 18 }} /> : null}
            </Box>
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
              Catálogo {titulo}
            </Typography>
          </Box>
          <IconButton
            onClick={cerrarCatalogo}
            size="small"
            disabled={guardando || eliminando}
            sx={{ color: FP.color.textSecondary }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 2, md: 3 } }}>{cuerpo}</DialogContent>
      </Dialog>
      {dialogoAlta}
      {confirmacion}
    </>
  );
};

export default CatalogoIdDescripcion;
